import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dig from 'object-dig';
import moment from 'moment';
import clsx from 'clsx';
import { Doughnut } from 'react-chartjs-2';

import BusinessIcon from '@material-ui/icons/Business';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import { makeStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Chip from '@material-ui/core/Chip';
import LinkMaterial from '@material-ui/core/Link';

import Table from '~/components/Table/Table';
import tableStyles from '~/components/Table/Table/styles';
import Paper from '~/components/Paper';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import LinearProgress from '~/components/LinearProgress';
import InputControl from '~/components/Input/InputControl';
import InputSelectAsync from '~/components/Input/InputSelectAsync';
import InputMonthPicker from '~/components/Input/InputMonthPicker';

import formatNumber from '~/utils/Number/formatNumber';
import Request from '~/providers/Request';

import colors from '~/utils/colors';
import fetchAllCorporatePartner from '~/utils/fetchAllCorporatePartner';
import {
  reportUsersActives,
  reportPackagesActives,
  reportPackagesEspecialtyActives,
} from '~/config/routes';

const useStyles = makeStyles((theme) => ({
  cell: {
    color: '#ffffff',
    backgroundColor: theme.palette.purple.light,
  },
  hidden: {
    display: 'none',
  },
}));

function ShowCorporation() {
  const { t } = useTranslation();
  const classesTable = tableStyles();
  const classes = useStyles();

  const [corporation, setCorporation] = useState(null);
  const [dataInit, setDataInit] = useState(moment().startOf('month').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('month').format());

  const [user, setUser] = useState([]);
  const [dataPackages, setDataPackages] = useState([]);

  const [dataDoctorAllBySpecialty, setDataDoctorAllBySpecialty] = useState([]);

  const [dataAppointments, setDataAppointments] = useState([]);
  const [withAppointments, setWithAppointments] = useState(0);
  const [withoutAppointments, setWithoutAppointments] = useState(0);
  const [showPieAppointment, setShowPieAppointment] = useState(false);

  const [dataAppointmentsBySpecialty, setDataAppointmentsBySpecialty] =
    useState([]);

  const [loading, setLoading] = useState(false);

  const getPiePackagesBySpecialty = () => {
    const labels = [];
    const data = [];
    const pieColors = [];

    if (dataDoctorAllBySpecialty) {
      dataDoctorAllBySpecialty.forEach((element, index) => {
        labels.push(`${element.description}: ${element.total}`);
        data.push(element.total);
        pieColors.push(colors[index]);
      });
    }

    return {
      labels: labels,
      datasets: [
        {
          backgroundColor: pieColors,
          hoverBackgroundColor: pieColors,
          data: data,
        },
      ],
    };
  };

  const getPieAppointment = () => {
    let labels = [];
    let data = [];
    let pieColors = [];
    setShowPieAppointment(false);

    if (withoutAppointments > 0 || withAppointments > 0) {
      labels = [
        `${t(
          'dashboard.attributes.without_specialty'
        )}: ${withoutAppointments}`,
        `${t('dashboard.attributes.with_specialty')}: ${withAppointments}`,
      ];
      data = [withoutAppointments, withAppointments];
      pieColors = ['#40BDA7', '#336699'];
      setShowPieAppointment(true);
    }

    return {
      labels: labels,
      datasets: [
        {
          backgroundColor: pieColors,
          hoverBackgroundColor: pieColors,
          data: data,
        },
      ],
    };
  };

  const getPieAppointmentBySpecialty = () => {
    const labels = [];
    const data = [];
    const pieColors = [];

    if (dataAppointmentsBySpecialty) {
      dataAppointmentsBySpecialty.forEach((element, index) => {
        labels.push(`${element.description}: ${element.total}`);
        data.push(element.total);
        pieColors.push(colors[index]);
      });
    }

    return {
      labels: labels,
      datasets: [
        {
          backgroundColor: pieColors,
          hoverBackgroundColor: pieColors,
          data: data,
        },
      ],
    };
  };

  const pieOptions = {
    tooltips: {
      backgroundColor: 'rgb(255,255,255)',
      bodyFontColor: '#858796',
      borderColor: '#dddfeb',
      borderWidth: 1,
      xPadding: 15,
      yPadding: 15,
      displayColors: false,
      caretPadding: 10,
      callbacks: {
        label: function (tooltipItems, data) {
          let i;
          const label = [];
          const l = data.datasets.length;

          const total = data.datasets[0].data.reduce(
            (a, b) => Number(a) + Number(b),
            0
          );
          for (i = 0; i < l; i += 1) {
            const platform = data.labels[tooltipItems.index].split(':')[0];
            const value = data.datasets[0].data[tooltipItems.index];

            let percent = (value / total) * 100;
            percent = formatNumber(percent);

            label[i] = `${platform}: ${percent}%`;
          }
          return label;
        },
      },
    },
    legend: {
      display: true,
      labels: {
        fontSize: 16,
      },
    },
    cutoutPercentage: 60,
  };

  function createData(description, total) {
    return { description, total };
  }

  async function fetchDahsboards() {
    setLoading(true);
    Request.get(
      '/admin/dashboard/corporations',
      {
        corporation_id: corporation.corporation_id,
        start: dataInit,
        end: dataEnd,
      },
      { normalizeJSON: false }
    )
      .then((response) => {
        setLoading(false);

        setUser([
          createData(
            t('dashboard.attributes.users'),
            dig(response, 'usersActive')
          ),
        ]);

        setDataPackages([
          createData(
            t('constants.packages.doctor'),
            dig(response, 'doctorActive')
          ),
          createData(
            t('constants.packages.doctor_all'),
            dig(response, 'doctorAllActive')
          ),
          createData(
            t('constants.packages.driving'),
            dig(response, 'drivingActive')
          ),
          createData(
            t('constants.packages.emergency'),
            dig(response, 'emergencyActive')
          ),
          createData(
            t('constants.packages.users'),
            dig(response, 'usersPackActive')
          ),
        ]);

        setWithAppointments(dig(response, 'appointmentsWithSpecialty'));
        setWithoutAppointments(dig(response, 'appointmentsWithoutSpecialty'));
        setDataAppointments([
          createData(
            t('dashboard.attributes.without_specialty'),
            dig(response, 'appointmentsWithoutSpecialty')
          ),
          createData(
            t('dashboard.attributes.with_specialty'),
            dig(response, 'appointmentsWithSpecialty')
          ),
          createData(
            t('dashboard.attributes.count_appointments'),
            dig(response, 'appointments')
          ),
          createData(
            t('dashboard.attributes.count_appointments_request'),
            dig(response, 'appointmentsRequest')
          ),
        ]);

        const appointmentsBySpecialty = [];
        response.appointmentsBySpecialty.forEach((element) => {
          const data = createData(
            dig(element, 'specialty', 'name'),
            dig(element, 'count')
          );
          appointmentsBySpecialty.push(data);
        });
        setDataAppointmentsBySpecialty(appointmentsBySpecialty);

        const doctorAllBySpecialty = [];
        response.doctorAllBySpecialty.forEach((element) => {
          const data = createData(
            dig(element, 'specialty', 'name'),
            dig(element, 'count')
          );
          doctorAllBySpecialty.push(data);
        });
        setDataDoctorAllBySpecialty(doctorAllBySpecialty);
      })
      .catch((e) => {
        setLoading(false);
        console.log(e);
      });
  }

  useEffect(() => {
    if (corporation) {
      fetchDahsboards();
    }
  }, [dataInit, dataEnd, corporation]);

  useEffect(() => {
    async function getFirstCorporation() {
      fetchAllCorporatePartner(null, 0, (data) => {
        setCorporation(data.items[0]);
      });
    }
    getFirstCorporation();
  }, []);

  return (
    <>
      <Paper
        Icon={BusinessIcon}
        iconColor="blue"
        title={t('dashboard.tabs.corporations')}
        loading={false}
      >
        <GridContainer justify="center" alignItems="center">
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputControl
              name={'corporation'}
              label={t('dashboard.attributes.corporation')}
              value={dig(corporation, 'name')}
              inputComponent={InputSelectAsync}
              onChange={({ corporation }) => {
                setCorporation(corporation);
              }}
              InputProps={{
                isClearable: false,
                handleFetch: fetchAllCorporatePartner,
                createEnabled: false,
                error: false,
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputMonthPicker
              setStartDate={(date) => setDataInit(date)}
              setEndDate={(date) => setDataEnd(date)}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <GridContainer>
          {/* ------------------------ LEFT ------------------------ */}
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <Table
                  loading={loading}
                  size="small"
                  columns={
                    <>
                      <TableCell className={classes.cell}>
                        {t('dashboard.attributes.users_by_month')} {'  '}
                        <Chip
                          label={t('dashboard.attributes.view')}
                          component={LinkMaterial}
                          href={`${reportUsersActives.spec}?corporations=${dig(
                            corporation,
                            'id'
                          )}&start=${dataInit}&end=${dataEnd}`}
                          style={{ cursor: 'pointer' }}
                          target="_blank"
                          onDelete={() => {}}
                          deleteIcon={<OpenInNewIcon />}
                        />
                      </TableCell>
                      <TableCell align="right" className={classes.cell}>
                        {t('dashboard.attributes.total')}
                      </TableCell>
                    </>
                  }
                >
                  {user.map((row) => {
                    return (
                      <TableRow
                        key={row.description}
                        hover
                        className={classesTable.row}
                      >
                        <TableCell>{row.description}</TableCell>
                        <TableCell align="right">{row.total}</TableCell>
                      </TableRow>
                    );
                  })}
                </Table>
              </GridItem>
            </GridContainer>

            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <Table
                  emptyData={dataPackages.length === 0}
                  loading={loading}
                  size="small"
                  columns={
                    <>
                      <TableCell className={classes.cell}>
                        {t('dashboard.attributes.packages_by_month')}

                        {'  '}

                        <Chip
                          label={t('dashboard.attributes.view')}
                          component={LinkMaterial}
                          href={`${
                            reportPackagesActives.spec
                          }?corporation_id=${dig(
                            corporation,
                            'id'
                          )}&start=${dataInit}&end=${dataEnd}`}
                          style={{ cursor: 'pointer' }}
                          target="_blank"
                          onDelete={() => {}}
                          deleteIcon={<OpenInNewIcon />}
                        />
                      </TableCell>
                      <TableCell align="right" className={classes.cell}>
                        {t('dashboard.attributes.total')}
                      </TableCell>
                    </>
                  }
                >
                  {dataPackages.map((row) => {
                    return (
                      <TableRow
                        key={row.description}
                        hover
                        className={classesTable.row}
                      >
                        <TableCell>{row.description}</TableCell>
                        <TableCell align="right">{row.total}</TableCell>
                      </TableRow>
                    );
                  })}
                </Table>
              </GridItem>
            </GridContainer>

            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <Table
                  emptyData={dataDoctorAllBySpecialty.length === 0}
                  loading={loading}
                  size="small"
                  columns={
                    <>
                      <TableCell className={classes.cell}>
                        {t('dashboard.attributes.packages_specialty_by_month')}
                        {'  '}
                        <Chip
                          label={t('dashboard.attributes.view')}
                          component={LinkMaterial}
                          href={`${
                            reportPackagesEspecialtyActives.spec
                          }?corporation_id=${dig(
                            corporation,
                            'id'
                          )}&start=${dataInit}&end=${dataEnd}`}
                          style={{ cursor: 'pointer' }}
                          target="_blank"
                          onDelete={() => {}}
                          deleteIcon={<OpenInNewIcon />}
                        />
                      </TableCell>
                      <TableCell align="right" className={classes.cell}>
                        {t('dashboard.attributes.total')}
                      </TableCell>
                    </>
                  }
                >
                  {dataDoctorAllBySpecialty.map((row) => {
                    return (
                      <TableRow
                        key={row.description}
                        hover
                        className={classesTable.row}
                      >
                        <TableCell>{row.description}</TableCell>
                        <TableCell align="right">{row.total}</TableCell>
                      </TableRow>
                    );
                  })}
                </Table>
              </GridItem>
            </GridContainer>

            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <Doughnut
                  data={getPiePackagesBySpecialty}
                  options={pieOptions}
                />
              </GridItem>
            </GridContainer>
          </GridItem>
          {/* ------------------------ CLOSE LEFT ------------------------ */}

          {/* ------------------------ RIGHT ------------------------ */}
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <Table
                  emptyData={dataAppointments.length === 0}
                  loading={loading}
                  size="small"
                  columns={
                    <>
                      <TableCell className={classes.cell}>
                        {t('dashboard.attributes.appointments_by_month')}
                      </TableCell>
                      <TableCell align="right" className={classes.cell}>
                        {t('dashboard.attributes.total')}
                      </TableCell>
                    </>
                  }
                >
                  {dataAppointments.map((row) => {
                    return (
                      <TableRow
                        key={row.description}
                        hover
                        className={classesTable.row}
                      >
                        <TableCell>{row.description}</TableCell>
                        <TableCell align="right">{row.total}</TableCell>
                      </TableRow>
                    );
                  })}
                </Table>
              </GridItem>
            </GridContainer>

            <div className={clsx(!showPieAppointment && classes.hidden)}>
              <GridContainer>
                <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Doughnut data={getPieAppointment} options={pieOptions} />
                </GridItem>
              </GridContainer>
            </div>

            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <Table
                  emptyData={dataAppointmentsBySpecialty.length === 0}
                  loading={loading}
                  size="small"
                  columns={
                    <>
                      <TableCell className={classes.cell}>
                        {t(
                          'dashboard.attributes.appointments_specialty_by_month'
                        )}
                      </TableCell>
                      <TableCell align="right" className={classes.cell}>
                        {t('dashboard.attributes.total')}
                      </TableCell>
                    </>
                  }
                >
                  {dataAppointmentsBySpecialty.map((row) => {
                    return (
                      <TableRow
                        key={row.description}
                        hover
                        className={classesTable.row}
                      >
                        <TableCell>{row.description}</TableCell>
                        <TableCell align="right">{row.total}</TableCell>
                      </TableRow>
                    );
                  })}
                </Table>
              </GridItem>
            </GridContainer>

            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <Doughnut
                  data={getPieAppointmentBySpecialty}
                  options={pieOptions}
                />
              </GridItem>
            </GridContainer>
          </GridItem>
          {/* ------------------------ CLOSE RIGHT ------------------------ */}
        </GridContainer>
      </Paper>
    </>
  );
}

export default ShowCorporation;
